/**
 * Pure function that decides whether a task-assignment sound + push
 * notification should fire, given the current `notifications` Firestore
 * snapshot and per-doc previously-seen tracking.
 *
 * Called by the taskNotifications store on every onSnapshot invocation.
 *
 * Design doc: docs/plans/2026-04-16-notification-settings-design.md
 * Unit tests: cypress/node-tests/task-notification-detection.test.js
 */

export const CURATED_NOTIFICATION_SOUNDS = [
  { filename: 'bell-123742.mp3', label: 'Bell' },
  { filename: 'simple-notification-152054.mp3', label: 'Soft Ding-Dong' },
  { filename: 'notification-4-126507.mp3', label: 'Soft Chime' },
  { filename: 'elevator-ding.mp3', label: 'Elevator Ding' },
  { filename: 'sound-alert-device-174892.mp3', label: 'Alert' },
];

export const DEFAULT_TASK_SOUND = 'bell-123742.mp3';

export function isCuratedSound(filename) {
  if (!filename || typeof filename !== 'string') return false;
  return CURATED_NOTIFICATION_SOUNDS.some(s => s.filename === filename);
}

/**
 * Resolve the sound filename to play for a task notification.
 * Falls back to the default if the setting is missing/invalid.
 *
 * @param {string|null|undefined} settingValue
 * @returns {string} A filename from the curated list
 */
export function resolveTaskSound(settingValue) {
  if (isCuratedSound(settingValue)) return settingValue;
  return DEFAULT_TASK_SOUND;
}

/**
 * Read the site-level sound+push enabled gate.
 * Defaults to `true` — missing/undefined means the feature is on.
 *
 * @param {string|boolean|null|undefined} settingValue
 * @returns {boolean}
 */
export function isTaskNotificationEnabled(settingValue) {
  if (settingValue === undefined || settingValue === null) return true;
  if (typeof settingValue === 'boolean') return settingValue;
  if (typeof settingValue === 'string') {
    const v = settingValue.toLowerCase().trim();
    if (v === 'false' || v === '0' || v === 'off' || v === 'no') return false;
    return true;
  }
  return true;
}

/**
 * Notification types this helper will fire sound + push for.
 * KANBAN was added 2026-04-16 when we added @-mention notifications to
 * Kanban cards; same pipeline as TASK, separate site-level gate.
 */
export const SUPPORTED_NOTIFICATION_TYPES = ['TASK', 'KANBAN'];

/**
 * @param {object} params
 * @param {Array} params.notifications          Notification docs from the current snapshot
 *                                              (each has { id, employeeID, type, text, createdAt, ... })
 * @param {object} params.prevSeenIds           { [notificationId]: true } — notifications already processed
 * @param {boolean} params.isFirstSnapshot      True on the first snapshot after listener setup
 * @param {string|null} params.currentUserID    Current employee's userID
 * @param {object} params.gates                 { TASK: boolean, KANBAN: boolean } — per-type site-level gates
 *                                              (if `soundPushEnabled` is passed legacy-style as a single boolean,
 *                                              it's treated as the TASK gate and KANBAN defaults to true)
 * @param {boolean} [params.soundPushEnabled]   LEGACY — single gate used only when `gates` is undefined.
 *                                              Applies to TASK only; KANBAN defaults to ON for backward compat.
 *
 * @returns {{
 *   nextSeenIds: object,
 *   fires: Array<{ id, type, text, createdAt }>
 * }}
 *
 * Returns one entry per genuinely-new inbound notification that should fire.
 * Caller decides whether to fire all or coalesce, and picks the sound file
 * based on `fire.type` (TASK vs KANBAN).
 */
export function detectTaskNotifications({
  notifications,
  prevSeenIds,
  isFirstSnapshot,
  currentUserID,
  gates,
  soundPushEnabled,
}) {
  const nextSeenIds = { ...(prevSeenIds || {}) };
  const fires = [];

  // Normalize gates — callers may pass the legacy `soundPushEnabled` (single bool)
  // or the new `gates` map. Default any missing type to ON.
  const resolvedGates = gates && typeof gates === 'object'
    ? gates
    : { TASK: soundPushEnabled !== false, KANBAN: true };

  if (!Array.isArray(notifications)) {
    return { nextSeenIds, fires };
  }

  notifications.forEach((n) => {
    if (!n || !n.id) return;

    // Record every doc we've seen — even on first snapshot or disabled gate —
    // so we don't re-fire later when the gate flips on.
    const alreadySeen = !!nextSeenIds[n.id];
    nextSeenIds[n.id] = true;

    if (alreadySeen) return;
    if (isFirstSnapshot) return;
    if (!currentUserID) return;

    if (!SUPPORTED_NOTIFICATION_TYPES.includes(n.type)) return;
    if (n.employeeID !== currentUserID) return;

    // Per-type gate check AFTER seen-ID recording so flipping gate on later
    // doesn't re-fire old notifications (they've already been marked seen).
    if (resolvedGates[n.type] === false) return;

    fires.push({
      id: n.id,
      type: n.type,
      text: n.text || (n.type === 'KANBAN' ? 'You have been mentioned on a Kanban card' : 'You have been assigned a task'),
      createdAt: n.createdAt || null,
    });
  });

  return { nextSeenIds, fires };
}
